import React, { useEffect, useState, useContext } from "react";
import {
  Box,
  Flex,
  Text,
  Button,
  Input,
  Select,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  Spinner,
  useToast,
  Badge,
  IconButton,
} from "@chakra-ui/react";
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import { Link, useNavigate } from "react-router-dom";
import { fetchAllAdminUsers } from "../../actions/users.action";
import { AuthContext } from "../../providers/AuthValidationLayer";
import { Unauthorized } from "../../components/Unauthorized";
import { Bold } from "lucide-react";
import BreadCrumbMaker from "../../components/BreadCrumbMaker";
import { Loading } from "../../components/Loading";

/* ---------------- SEARCH TH COMPONENT ---------------- */
const SearchTh = ({ value, onChange, placeholder }) => (
  <Th py={2}>
    <Input
      size="xs"
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      bg="white"
      border="1px solid #E2E8F0"
      borderRadius="6px"
      _focus={{ borderColor: "#4C7CF7", boxShadow: "none" }}
    />
  </Th>
);

/* ---------------- MAIN COMPONENT ---------------- */
export default function AdminsList() {
  const navigate = useNavigate();
  const toast = useToast();
  const { hasPermission } = useContext(AuthContext);

  const canViewAdmins = hasPermission("Access Control", "Employees", "GET");

  const [admins, setAdmins] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // Input filters - update immediately for smooth typing
  const [inputFilters, setInputFilters] = useState({
    name: "",
    email: "",
    role: "",
    status: "",
  });

  // Debounced filters - only trigger API call after user stops typing
  const [debouncedFilters, setDebouncedFilters] = useState(inputFilters);

  // Debounce logic: wait 300ms after user stops typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedFilters(inputFilters);
    }, 300);

    return () => clearTimeout(timer);
  }, [inputFilters]);

  /* ---------------- FETCH ADMINS ---------------- */
  const getAdmins = async (isInitial = false) => {
    try {
      if (isInitial) {
        setInitialLoading(true);
      } else {
        setSearching(true);
      }
      const res = await fetchAllAdminUsers(page, debouncedFilters, pageSize);
      setAdmins(res?.data || []);
      setTotalPages(res?.totalPages || 1);
    } catch (err) {
      toast({
        title: "Error",
        description: err.message || "Failed to fetch admins",
        status: "error",
      });
    } finally {
      setInitialLoading(false);
      setSearching(false);
    }
  };

  // Fetch on initial load
  useEffect(() => {
    getAdmins(true);
  }, []);

  // Fetch when page changes
  useEffect(() => {
    if (!initialLoading) {
      getAdmins(false);
    }
  }, [page]);

  // Reset to page 1 and fetch when filters change
  useEffect(() => {
    if (!initialLoading) {
      if (page === 1) {
        getAdmins(false);
      } else {
        setPage(1);
      }
    }
  }, [debouncedFilters]);

  useEffect(() => {
    if (pageSize !== 20 && pageSize !== 50) {
      setPageSize(20);
    }
  }, [pageSize]);

  useEffect(() => {
    if (!initialLoading) {
      setPage(1);
      getAdmins(false);
    }
  }, [pageSize]);

  if (!canViewAdmins) return <Unauthorized />;

  if (initialLoading) return <Loading />;

  return (
    <Box w="100%" bg="#F5F7FA" p={6}>
      {/* BREADCRUMB */}
      <BreadCrumbMaker
        items={[
          { title: "Access Control", link: "/settings" },
          { title: "Manage Employee", link: "/settings/admin" },
        ]}
      />

      {/* HEADER */}
      <Flex justify="space-between" align="center" mt={2} mb={4}>
        <Text fontSize="2xl" fontWeight="700">
          Employee List
        </Text>

        <Button
          as={Link}
          to="/settings/admin/create?source=manage-employees"
          bg="#4C7CF7"
          color="white"
          px={7}
          h="44px"
          borderRadius="10px"
          _hover={{ bg: "#3b6ae0" }}
        >
          + Add New Employee
        </Button>
      </Flex>

      {/* TABLE CARD */}
      <Box
        bg="white"
        borderRadius="16px"
        border="1px solid #E5E7EB"
        boxShadow="0 2px 8px rgba(0,0,0,0.04)"
        overflow="hidden"
        position="relative"
      >
        {searching && (
          <Box
            position="absolute"
            top="0"
            left="0"
            right="0"
            bottom="0"
            bg="rgba(255,255,255,0.7)"
            display="flex"
            alignItems="center"
            justifyContent="center"
            zIndex={10}
          >
            <Spinner size="lg" color="#4C7CF7" />
          </Box>
        )}
        <Table size="sm">
          <Thead bg="#F9FAFB">
            {/* COLUMN HEADERS */}
            <Tr>
              <Th w="80px">Sr. No</Th>
              <Th>Name</Th>
              <Th>Email</Th>
              <Th w="160px">Role</Th>
              <Th w="120px" textAlign="center">
                Status
              </Th>
            </Tr>

            {/* SEARCH ROW */}
            <Tr bg="#E0EAFF">
              <Th />

              <SearchTh
                placeholder="Search name"
                value={inputFilters.name}
                onChange={(e) =>
                  setInputFilters({ ...inputFilters, name: e.target.value })
                }
              />

              <SearchTh
                placeholder="Search email"
                value={inputFilters.email}
                onChange={(e) =>
                  setInputFilters({
                    ...inputFilters,
                    email: e.target.value,
                  })
                }
              />

              <SearchTh
                placeholder="Role"
                value={inputFilters.role}
                onChange={(e) =>
                  setInputFilters({ ...inputFilters, role: e.target.value })
                }
              />

              <Th py={2}>
                <Select
                  size="xs"
                  value={inputFilters.status}
                  onChange={(e) =>
                    setInputFilters({
                      ...inputFilters,
                      status: e.target.value,
                    })
                  }
                  bg="white"
                  border="1px solid #E2E8F0"
                  borderRadius="6px"
                  _focus={{ borderColor: "#4C7CF7", boxShadow: "none" }}
                >
                  <option value="">All Status</option>
                  <option value="active">Active</option>
                  <option value="suspended">Suspended</option>
                </Select>
              </Th>
            </Tr>
          </Thead>

          <Tbody>
            {admins.length === 0 ? (
              <Tr>
                <Td colSpan={5} textAlign="center" py={8} color="gray.500">
                  No admins found
                </Td>
              </Tr>
            ) : (
              admins.map((admin, index) => (
                <Tr
                  key={admin._id}
                  _hover={{ bg: "#F5F8FF" }}
                  transition="0.2s"
                  cursor="pointer"
                  onClick={() => navigate(`/settings/admin/${admin._id}`)}
                >
                  <Td>
                    {String((page - 1) * pageSize + index + 1).padStart(2, "0")}
                    .
                  </Td>

                  <Td fontWeight="500" maxW="220px">
                    <Text isTruncated title={admin.name}>
                      {admin.name}
                    </Text>
                  </Td>

                  <Td color="gray.600" maxW="260px">
                    <Text isTruncated title={admin.email}>
                      {admin.email}
                    </Text>
                  </Td>

                  <Td>
                    <Badge
                      px={3}
                      py={1}
                      borderRadius="full"
                      bg="#E9EEFF"
                      color="#4C7CF7"
                      fontSize="12px"
                    >
                      {admin.roleId?.roleName || "—"}
                    </Badge>
                  </Td>

                  <Td textAlign="center">
                    <Badge
                      px={3}
                      py={1}
                      borderRadius="full"
                      bg={admin.isSuspended ? "#FEE2E2" : "#DCFCE7"}
                      color={admin.isSuspended ? "#B91C1C" : "#15803D"}
                      fontSize="12px"
                    >
                      {admin.isSuspended ? "Suspended" : "Active"}
                    </Badge>
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>

        {/* PAGINATION */}
        <Flex
          justify="space-between"
          align="center"
          p={4}
          borderTop="1px solid #E5E7EB"
        >
          <Text fontSize="sm" color="gray.600">
            Page <b>{page}</b> of <b>{totalPages}</b>
          </Text>

          <Flex align="center" gap={2}>
            <Text fontSize="sm">Rows per page:</Text>
            <Select
              size="sm"
              width="80px"
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
            >
              <option value="20">20</option>
              <option value="50">50</option>
            </Select>
          </Flex>

          <Flex gap={2}>
            <IconButton
              size="sm"
              isDisabled={page === 1}
              onClick={() => setPage((p) => p - 1)}
              icon={<ChevronLeftIcon />}
              variant="outline"
            />
            <IconButton
              size="sm"
              isDisabled={page === totalPages}
              onClick={() => setPage((p) => p + 1)}
              icon={<ChevronRightIcon />}
              variant="outline"
            />
          </Flex>
        </Flex>
      </Box>
    </Box>
  );
}
